import React, { useState, useEffect, useCallback } from "react";
import { IGameModel, IGameModelSimulation } from "../../types/IClubDetail";
import { useLocation, useNavigate } from "react-router-dom";
import { useSwapHistory } from "../../contexts/SwapHistoryContext";
import apiUrls from "../../config/apiUrls";
import { postData } from "../../services/api";
import IApiResponse from "../../types/IApiResponse";
import { IPreviewPlayerCard } from "../../types/IPlayerCardDetail";
import handleApiResponse from "../../utils/handleApiResponse";
import {
  ChemistryPlayerPositions,
  FormationData,
  TeamChemistry,
  TeamPasses,
} from "../../types/IOption";
import { MatchBasicDetails } from "../Chemistry/Utils";
import ClubHeaderPDF from "./ClubHeaderPDF";
import PitchForPDF from "./PitchForPDF";
import SystemFitValuesAndContent from "./SystemFitValuesAndContent";
import GameModelValuesAndContent from "./GameModelValuesAndContent";
import PossessionValuesAndContent from "./PossessionValuesAndContent";
import PrimaryButton from "../../ui/PrimaryButton";
import LabelText from "../../ui/LabelText";
import PreviewPlayerCard from "./PreviewPlayerCard";
import ChevronLeft from "../../assets/icons/ChevronLeft.png";
import styled from "styled-components";
import { formatNumberToKAndM } from "../../utils/formatNumberToKandM";
import LabelNumber from "../../styled-components/LabelNumber";
import UpArrow from '../../assets/icons/ArrowUp.png';
import DownArrow from '../../assets/icons/ArrowDown.png';
import { hasPlayersFromDifferentClubs } from "../../utils/drawChemistrys";
interface PreviewProps {}

// Create a custom styled button that matches PrimaryButton styling
const BackButton = styled.button`
    height: fit-content;
    display: flex;
    padding: 8px 16px 8px 12px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 4px;
    border: 1px solid var(--Shades-Brand-Green-1-green-1-600, #00BE99);
    background: var(--Brand-green-1-500, #00D1A8);
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: #000;
    transition: all 0.2s ease;

    &:hover {
        border: 1px solid var(--Shades-Brand-Green-1-green-1-700, #009477);
        background: var(--Shades-Brand-Green-1-green-1-600, #00BE99);
    }

    &:active {
        transform: translateY(1px);
    }
`;

const Preview: React.FC<PreviewProps> = () => {
  const navigate = useNavigate();
  const { clearSwapHistory } = useSwapHistory();
  const [fetchedSwapDetails, setFetchedSwapDetails] = useState<any[]>([]);
  const [fetchedGameModelData, setFetchedGameModelData] = useState<IGameModelSimulation | null>(null);
  const [isGameModelLoading, setIsGameModelLoading] = useState<boolean>(false);
  const [currentClubName, setCurrentClubName] = useState<string>('');

  const { teamId } = useLocation().state as { teamId: number };
  const { gameModelData } = useLocation().state as {gameModelData: IGameModel[] | IGameModelSimulation;};
  const { possessionPlayers } = useLocation().state as {possessionPlayers: ChemistryPlayerPositions[];};
  const { positionResults } = useLocation().state as {positionResults: ChemistryPlayerPositions[];};
  const { teamChemistry } = useLocation().state as {teamChemistry: TeamChemistry[];};
  const { possessionPasses } = useLocation().state as {possessionPasses: TeamPasses[];};
  const { selectedSimulationTab } = useLocation().state as {selectedSimulationTab: string;};
  const { formationData } = useLocation().state as {formationData: FormationData[]};
  const { selectedFormation } = useLocation().state as {selectedFormation: number};

  // Get values boxes data
  const { teamAttributesData } = useLocation().state as {teamAttributesData?: any};
  const { simulationAttributesData } = useLocation().state as {simulationAttributesData?: any};
  const { teamFinancialSimulationData } = useLocation().state as {teamFinancialSimulationData?: any};
  const { overallSystemFitScore } = useLocation().state as {overallSystemFitScore?: number};
  const { overAllswappSystemFitScore } = useLocation().state as {overAllswappSystemFitScore?: number};
  const { inPlayerIds } = useLocation().state as {inPlayerIds?: number[]};
  const { outPlayerIds } = useLocation().state as {outPlayerIds?: number[]};
  const { squadPlayerComparisonData } = useLocation().state as {squadPlayerComparisonData?: any};
  const { simulationData } = useLocation().state as {simulationData?: any};
  const { comparisonTeamData } = useLocation().state as {comparisonTeamData?: any[]};
  const { clubCompareId } = useLocation().state as {clubCompareId?: number};

  // Additional props needed for SystemFitValuesAndContent (computed values, no functions)
  const { activeArcheTypeName } = useLocation().state as {activeArcheTypeName?: string};
  const { archetypes } = useLocation().state as {archetypes?: any[]};
  const { isCompareBalckLine } = useLocation().state as {isCompareBalckLine?: boolean};
  const { swapDataLoading } = useLocation().state as {swapDataLoading?: boolean};
  const { compareClubLogoUrl } = useLocation().state as {compareClubLogoUrl?: string};
  const { compareClubName } = useLocation().state as {compareClubName?: string};
  const { compareClubseasonLabel } = useLocation().state as {compareClubseasonLabel?: string};
  const { stylisticFitValue } = useLocation().state as {stylisticFitValue?: number | ""};
  const { stylisticFitForCoachValue } = useLocation().state as {stylisticFitForCoachValue?: number | ""};
  const { compareStylisticFitValue } = useLocation().state as {compareStylisticFitValue?: number | ""};
  const { afterScoreValue } = useLocation().state as {afterScoreValue?: number};
  const { improvementValue } = useLocation().state as {improvementValue?: number};
  const { clubNameValue } = useLocation().state as {clubNameValue?: string};
  const { seasonNameValue } = useLocation().state as {seasonNameValue?: string};
  const { clubNameForCoachValue } = useLocation().state as {clubNameForCoachValue?: string};
  const { seasonNameForCoachValue } = useLocation().state as {seasonNameForCoachValue?: string};
  const { playingStyleForCoachValue } = useLocation().state as {playingStyleForCoachValue?: string};
  const { targetedFormationValue } = useLocation().state as {targetedFormationValue?: string};
  const { targetedSeasonNameValue } = useLocation().state as {targetedSeasonNameValue?: string};
  const { targetTeamIdValue } = useLocation().state as {targetTeamIdValue?: number};
  const { targetedArcheTypeFormation } = useLocation().state as {targetedArcheTypeFormation?: string};
  const { targetedFormation } = useLocation().state as {targetedFormation?: string};
  const { activeTeamGraph } = useLocation().state as {activeTeamGraph?: string};
  const { archeTypeBy } = useLocation().state as {archeTypeBy?: string};

  const { matchBasicDetails } = useLocation().state as {matchBasicDetails?: MatchBasicDetails | null};
  const { selectedGameModelAttributes: stateSelectedGameModelAttributes } = useLocation().state as {selectedGameModelAttributes?: string[]};

  const { seasonLabel } = useLocation().state as {seasonLabel?: string};
  const { coachName } = useLocation().state as {coachName?: string};
  const { activeformation } = useLocation().state as {activeformation?: string};
  const { swapHistory } = useLocation().state as {swapHistory?: any[]};
  const { userData } = useLocation().state as {userData?: any};
  const { seasonId } = useLocation().state as {seasonId?: number};

  // Fetch player details for swap history
  useEffect(() => {
    const fetchPlayerCardDataWithUserId = async (
      playerId: number,
      userId: number
    ): Promise<IPreviewPlayerCard | null> => {
      try {
        const response: IApiResponse<IPreviewPlayerCard> = await postData(
          `${apiUrls["GetPlayerCardDetail"]}`,
          {
            playerId: playerId,
            userId: userId
          }
        );

        return new Promise((resolve) => {
          handleApiResponse(
            response,
            (playerData) => {
              resolve({
                playerId: playerData.playerId,
                givenName: playerData.givenName,
                familyname: playerData.familyname,
                playerImageURL: playerData.playerImageURL,
                isInjured: playerData.isInjured,
                onLoan: playerData.onLoan,
                givenOnLoan: playerData.givenOnLoan,
                primaryPlayingPosition: playerData.primaryPlayingPosition,
                age: playerData.age,
                heightInCM: playerData?.heightInCM ?? playerData?.heightInCm,
                playerAttributesByPosition: playerData.playerAttributesByPosition,
                contributionValue: playerData.contributionValue,
                performanceValue: playerData.performanceValue,
                clubImageURL: playerData?.clubImageURL,
                clubId: playerData?.clubId,
                isPlayerInShortlist: playerData.isPlayerInShortlist,
                playerLabel: playerData.playerLabel,
                isEasternPlayer: playerData.isEasternPlayer,
                ...(playerData?.archetypes?.length > 0 && {
                  archetypeName: playerData.archetypes[0].archetypeName,
                  archetypeId: playerData.archetypes[0].id
                }),
                trueValueText: playerData?.trueValueText ? playerData.trueValueText : '',
                wagesPWText: playerData?.wagesPWText ? playerData.wagesPWText : (playerData?.wagesPW ? `€${(playerData.wagesPW/1000).toFixed(0)}K/wk` : undefined),
                playerAgent: playerData?.playerAgent,
                clubName: playerData?.clubName,
                nationalityCountry: playerData?.nationalityCountry,
                nationalityCountryCode: playerData?.nationalityCountryCode
              });
            },
            (error) => {
              console.error("Error fetching Player Card Data:", error);
              resolve(null);
            }
          );
        });
      } catch (error) {
        console.error("Error fetching Player Card Data:", error);
        return null;
      }
    };

    const fetchSwapPlayerDetails = async () => {
      if (swapHistory && swapHistory.length > 0) {
        const fetchPromises = swapHistory.map(async (swap: any) => {
          const userId = userData?.userId || 1;

          // Fetch both player details concurrently
          const [outPlayer, inPlayer] = await Promise.all([
            fetchPlayerCardDataWithUserId(swap.outPlayerId, userId),
            fetchPlayerCardDataWithUserId(swap.inPlayerId, userId)
          ]);

          if (outPlayer && inPlayer) {
            return {
              ...swap,
              outPlayer,
              inPlayer
            };
          }
          return null;
        });

        const results = await Promise.all(fetchPromises);
        setFetchedSwapDetails(results.filter(result => result !== null));
      }
    };

    fetchSwapPlayerDetails();
  }, [swapHistory, userData]);

  const fetchGameModelData = useCallback(async () => {
    // Don't fetch if we already have valid data
    if (gameModelData && (Array.isArray(gameModelData) || (gameModelData as IGameModelSimulation).inGameModel?.length > 0)) {
      return;
    }

    setIsGameModelLoading(true);
    try {
      const currentSwapHistory = swapHistory || [];
      const attributes = stateSelectedGameModelAttributes || ["crosses", "passes_to_final_third", "shots", "defensive_duels"];

      if (currentSwapHistory.length > 0) {
        // When there are swaps, use GetGameModelSimulation API
        const currentInPlayerIds = currentSwapHistory.map(swap => swap.inPlayerId);
        const currentOutPlayerIds = currentSwapHistory.map(swap => swap.outPlayerId);

        const response: IApiResponse<IGameModelSimulation> = await postData(`${apiUrls["GetGameModelSimulation"]}`, {
          seasonId: seasonId,
          plotAll: true,
          teamId: Number(teamId),
          typeFilter: attributes,
          inPlayerId: currentInPlayerIds,
          outPlayerId: currentOutPlayerIds
        });

        handleApiResponse(
          response,
          (gameModelSimulationData) => {
            setIsGameModelLoading(false);
            setFetchedGameModelData(gameModelSimulationData);
          },
          (error) => {
            setIsGameModelLoading(false);
            console.error("Error fetching Game Model Simulation Data:", error);
          }
        );
      } else {
        // When no swaps, use GetGameModel API
        const response: IApiResponse<IGameModel[]> = await postData(`${apiUrls["GetGameModel"]}`, {
          seasonId: seasonId,
          teamId: Number(teamId),
          typeFilter: attributes,
          userId: userData?.userId || userData?.id || 1
        });

        handleApiResponse(
          response,
          (gameModelData) => {
            setIsGameModelLoading(false);
            // Convert single GameModel to simulation format for consistency
            const simulationData: IGameModelSimulation = {
              inPlayersDetail: [],
              outPlayersDetail: [],
              inGameModel: gameModelData,
              outGameModel: gameModelData
            };
            setFetchedGameModelData(simulationData);
          },
          (error) => {
            setIsGameModelLoading(false);
            console.error("Error fetching Game Model Data:", error);
          }
        );
      }
    } catch (error) {
      setIsGameModelLoading(false);
      console.error("Unexpected error while fetching Game Model Data:", error);
    }
  }, [gameModelData, swapHistory, seasonId, teamId, stateSelectedGameModelAttributes, userData?.userId, userData?.id]);

  // Fetch GameModel data when component mounts if needed
  useEffect(() => {
    fetchGameModelData();
  }, [fetchGameModelData]);

  // Get the correct GameModel data to use
  const getGameModelData = (): IGameModel[] | IGameModelSimulation | null => {
    if (gameModelData && (Array.isArray(gameModelData) || (gameModelData as IGameModelSimulation).inGameModel?.length > 0)) {
      return gameModelData;
    }
    return fetchedGameModelData;
  };

  // Get the correct selectedGameModelAttributes
  const getSelectedGameModelAttributes = (): string[] => {
    return stateSelectedGameModelAttributes || ["crosses", "passes_to_final_third", "shots", "defensive_duels"];
  };

  const hasSwaps = fetchedSwapDetails && fetchedSwapDetails.length > 0;

  const getSwapSummaryText = () => {
    if (!hasSwaps) return "";

    const clubDisplayName = currentClubName || "this club";

    const inPlayersList = fetchedSwapDetails
      .map((swap: any) => {
        const g = swap?.inPlayer?.givenName;
        const f = swap?.inPlayer?.familyname;
        if (!g && !f) return null;
        return [g, f].filter(Boolean).join(" ");
      })
      .filter(Boolean);

    const outPlayersList = fetchedSwapDetails
      .map((swap: any) => {
        const g = swap?.outPlayer?.givenName;
        const f = swap?.outPlayer?.familyname;
        if (!g && !f) return null;
        return [g, f].filter(Boolean).join(" ");
      })
      .filter(Boolean);

    // Find common players that exist in both lists
    const commonPlayers = inPlayersList.filter(player => outPlayersList.includes(player));

    // Remove common players from both lists
    const filteredInPlayers = inPlayersList.filter(player => !commonPlayers.includes(player));
    const filteredOutPlayers = outPlayersList.filter(player => !commonPlayers.includes(player));

    if (filteredInPlayers.length === 0 || filteredOutPlayers.length === 0) {
      return `Using our machine learning models, we simulated how various performance and financial metrics of ${clubDisplayName} would change when player swaps were made.`;
    }

    return {
      text: `Using our machine learning models, we simulated how various performance and financial metrics of `,
      clubName: clubDisplayName,
      middleText1: ` would change if `,
      inPlayersList: filteredInPlayers,
      middleText2: ` were brought in to replace `,
      outPlayersList: filteredOutPlayers,
      endText: `.`
    };
  };

  const handleGoBack = () => {
    clearSwapHistory();
    navigate(-1);
  };

  // Clear swap history when component unmounts or user navigates away via browser
  useEffect(() => {
    const handlePopState = () => {
      clearSwapHistory();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearSwapHistory();
    };
  }, [clearSwapHistory]);

  const handlePrintToPDF = () => {
    // Get the Print PDF button and hide it before printing
    const printButton = document.querySelector(".pdfPrintBtn");
    const backButton = document.querySelector(".goBackBtn");
    let prevDisplay: any;
    let prevBackDisplay: any;
    if (printButton instanceof HTMLElement) {
      prevDisplay = printButton.style.display;
      printButton.style.display = "none";
    }
    if (backButton instanceof HTMLElement) {
      prevBackDisplay = backButton.style.display;
      backButton.style.display = "none";
    }
    // Add 150px margin to the element with id='pitch-attributes-for-pitch' before printing
    // const pitchAttributesElement = document.getElementById('pitch-attributes-for-pitch');
    // let prevMargin = '';
    // if (pitchAttributesElement) {
    //   prevMargin = pitchAttributesElement.style.marginTop;
    //   pitchAttributesElement.style.marginTop = '150px';
    // }
    // // Print and cleanup margin after printing
    // setTimeout(() => {
    //   if (pitchAttributesElement) {
    //     pitchAttributesElement.style.marginTop = prevMargin;
    //   }
    // }, 1100);
    // After printing, show the buttons again
    setTimeout(() => {
      if (printButton instanceof HTMLElement) {
        printButton.style.display = prevDisplay !== undefined ? prevDisplay : "";
      }
      if (backButton instanceof HTMLElement) {
        backButton.style.display = prevBackDisplay !== undefined ? prevBackDisplay : "";
      }
    }, 1000);
    // Calculate dynamic page height based on content
    const calculatePageHeight = () => {
      const mainContainer = document.querySelector('.cmn-main-container') as HTMLElement;
      if (mainContainer) {
        const contentHeight = mainContainer.scrollHeight;
        // Convert pixels to mm (1mm ≈ 3.78px at 96 DPI)
        const heightInMm = Math.ceil((contentHeight / 3.78) + 100); // Add 100mm buffer
        // Remove the cap for very long content
        return heightInMm;
      }
      return 2000; // Fallback height
    };

    const dynamicPageHeight = calculatePageHeight();

    // Create a style element for dynamic paper size with NO page breaks
    const style = document.createElement("style");
    style.innerHTML = `
                @media print {
                  @page {
                    size: 420mm ${dynamicPageHeight}mm;
                    margin: 0;
                  }
                  * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                  }
                  body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                    background: #F9FAF7 !important;
                    width: 420mm;
                    height: ${dynamicPageHeight}mm;
                    overflow: visible;
                  }

                  /* PREVENT ALL PAGE BREAKS - Force single page */
                  * {
                    page-break-before: avoid !important;
                    page-break-after: avoid !important;
                    page-break-inside: avoid !important;
                    break-before: avoid !important;
                    break-after: avoid !important;
                    break-inside: avoid !important;
                  }

                  /* Specific overrides for any automatic page breaks */
                  .swap-history-container,
                  .player-swap-item,
                  .club-header-pdf,
                  .pitch-for-pdf,
                  .system-fit-values,
                  .game-model-values,
                  .possession-values,
                  div,
                  section,
                  article {
                    page-break-before: avoid !important;
                    page-break-after: avoid !important;
                    page-break-inside: avoid !important;
                    break-before: avoid !important;
                    break-after: avoid !important;
                    break-inside: avoid !important;
                  }

                  /* Ensure content flows continuously */
                  .cmn-main-container {
                    height: auto !important;
                    overflow: visible !important;
                  }
                }
              `;
    document.head.appendChild(style);
    // Trigger browser print dialog
    window.print();
    // Clean up the style element after printing
    setTimeout(() => {
      document.head.removeChild(style);
    }, 1000);
  };

  const getDifferenceValue = (beforeValue: number, afterValue: number, isAge: boolean = false) => {
    const diff = afterValue - beforeValue;
    return isAge ? diff.toFixed(1) : diff.toFixed(2);
  };

  return (
    <div className="cmn-main-container" style={{backgroundColor: "#F9FAF7", paddingBottom:'40px'}}>
      <div style={{ position: 'absolute', top: '40px', right: '150px', zIndex: 1000 }}>
        <BackButton
          className="goBackBtn"
          onClick={handleGoBack}
        >
          <img src={ChevronLeft} alt="back" style={{ width: '16px', height: '16px' }} />
          <LabelText className="primary-cta-text">Back</LabelText>
        </BackButton>
      </div>
      <div style={{ position: 'absolute', top: '40px', right: '40px', zIndex: 1000 }}>
        <PrimaryButton
          className="pdfPrintBtn"
          label="Save PDF"
          onClick={() => {
            handlePrintToPDF();
          }}
        />
      </div>

      <div className="club-header-pdf">
        <ClubHeaderPDF teamId={Number(teamId)} setCurrentClubName={setCurrentClubName} />
      </div>

      {/* Swap History Section */}
      {(selectedSimulationTab === 'systemFit' || selectedSimulationTab === 'gameModel') && hasSwaps && (
        <>
          <div style={{
            width: '100%',
            maxWidth: '1065px',
            margin: '80px auto 0',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <p style={{width: '100%', fontFamily: 'Aeonik, sans-serif', fontSize: '20px', fontStyle: 'normal', fontWeight: 300, color:'#7C7D79', lineHeight: '28px'}} className="valuegraph-toggle-text">
              {(() => {
                const summary = getSwapSummaryText();
                if (typeof summary === 'string') {
                  return summary;
                }
                return (
                  <>
                    {summary.text}
                    <span style={{ fontWeight: 600, color: '#1A472A', backgroundColor: '#E8F5E8', padding: '2px 8px', borderRadius: '4px', margin: '0 2px' }}>
                      {summary.clubName}
                    </span>
                    {summary.middleText1}
                    {summary.inPlayersList.map((player, index) => (
                      <React.Fragment key={`in-${index}`}>
                        <span style={{ fontWeight: 600, color: '#2C3E50', backgroundColor: '#F0F4F8', padding: '2px 6px', borderRadius: '4px', margin: '0 2px' }}>
                          {player}
                        </span>
                        {index < summary.inPlayersList.length - 2 && ', '}
                        {index === summary.inPlayersList.length - 2 && ' and '}
                      </React.Fragment>
                    ))}
                    {summary.middleText2}
                    {summary.outPlayersList.map((player, index) => (
                      <React.Fragment key={`out-${index}`}>
                        <span style={{ fontWeight: 600, color: '#2C3E50', backgroundColor: '#FFF4E6', padding: '2px 6px', borderRadius: '4px', margin: '0 2px' }}>
                          {player}
                        </span>
                        {index < summary.outPlayersList.length - 2 && ', '}
                        {index === summary.outPlayersList.length - 2 && ' and '}
                      </React.Fragment>
                    ))}
                    {summary.endText}
                  </>
                );
              })()}
            </p>
          </div>

          <div className="swap-history-container" style={{
            width: '100%',
            maxWidth: '1050px',
            margin: '20px auto 40px',
            display: 'flex',
            flexDirection: 'column',
            gap: '25px',
            backgroundColor: "#F2F5EE",
            padding: "10px",
            borderRadius: "8px",
            border: "1px solid #DADADA"
          }}>
          {fetchedSwapDetails.map((swap: any, index: number) => (
            <div className="player-swap-item" key={swap.swapId || index} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '15px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E0E0E0'
            }}>
              <LabelText className="secondary-cta-text" style={{ fontSize: '16px', fontWeight: '600' }}>
                Swap {index + 1}
              </LabelText>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '25px',
                justifyContent: 'center'
              }}>

                {/* In Player */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <LabelText className="secondary-cta-text" style={{ fontSize: '14px', color: '#666' }}>
                  In
                </LabelText>
                <PreviewPlayerCard
                  {...swap.inPlayer}
                />
                </div>

                {/* Arrow */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '100px',
                    height: '4px',
                    backgroundColor: '#64ADDB'
                  }}></div>
                  <div style={{
                    width: '0',
                    height: '0',
                    borderLeft: '32px solid #64ADDB',
                    borderTop: '12px solid transparent',
                    borderBottom: '12px solid transparent'
                  }}></div>
                </div>
                {/* Out Player */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                  <LabelText className="secondary-cta-text" style={{ fontSize: '14px', color: '#666' }}>
                    Out
                  </LabelText>
                  <PreviewPlayerCard
                    {...swap.outPlayer}
                  />
                </div>
              </div>
            </div>
          ))}
          </div>
        </>
      )}

      {(selectedSimulationTab === 'systemFit' || selectedSimulationTab === 'gameModel') && (
        <div style={{width: '1065px', marginTop: "20px", display: 'flex', flexDirection: "column", gap: "10px", backgroundColor: "#F2F5EE", padding: "8px", borderRadius: "8px", border: "1px solid #DADADA", marginInline: 'auto'}}>
          <div className='values-container'>
            <div className='values-box' style={{ backgroundColor: "white", height: 'auto', width: "100%" }}>
              {(() => {
                const squadValueDiff = Number(getDifferenceValue(teamFinancialSimulationData?.beforValue?.squadValue || 0, teamFinancialSimulationData?.afterValue?.squadValue || 0));
                const totalWagesDiff = Number(getDifferenceValue(teamFinancialSimulationData?.beforValue?.totalWages || 0, teamFinancialSimulationData?.afterValue?.totalWages || 0));
                const avgAgeDiff = Number(getDifferenceValue(teamFinancialSimulationData?.beforValue?.avgAgeValue || 0, teamFinancialSimulationData?.afterValue?.avgAgeValue || 0, true));
                return (
                  <div className='values-box-content'>
                    <div className='content-value' >
                      <LabelText className="secondary-cta-text">Squad value</LabelText>
                      <div className='pitch-selection-container' style={{ padding: '0px' }}>
                        <div>
                          {inPlayerIds && inPlayerIds.length > 0 && outPlayerIds && outPlayerIds.length > 0 && squadValueDiff !== 0 && hasPlayersFromDifferentClubs({positionResults, teamId}) && (
                            <>
                              <img height={14} width={10} src={squadValueDiff > 0 ? UpArrow : DownArrow} alt="trend arrow" className={squadValueDiff > 0 ? "arrow-up-bad" : "arrow-down-good"} />
                              <LabelNumber className='event-player-card-details' style={{ color: squadValueDiff > 0 ? "#FF3864" : "#00D1A8" }}>
                                {formatNumberToKAndM(Math.abs(squadValueDiff))}
                              </LabelNumber>
                            </>
                          )}
                        </div>
                        <div className="values-content">
                          <LabelNumber className="event-player-card-details">
                            €{teamFinancialSimulationData?.beforValue?.squadValueText || '--'}
                          </LabelNumber>
                        </div>
                      </div>
                    </div>
                    <div className='content-value'>
                      <LabelText className="secondary-cta-text">Total wages pw</LabelText>
                      <div className='pitch-selection-container' style={{ padding: '0px' }}>
                        <div>
                          {inPlayerIds && inPlayerIds.length > 0 && outPlayerIds && outPlayerIds.length > 0 && totalWagesDiff !== 0 && hasPlayersFromDifferentClubs({positionResults, teamId}) && (
                            <>
                              <img height={14} width={10} src={totalWagesDiff > 0 ? UpArrow : DownArrow} alt="trend arrow" className={totalWagesDiff > 0 ? "arrow-up-bad" : "arrow-down-good"} />
                              <LabelNumber className='event-player-card-details' style={{ color: totalWagesDiff > 0 ? "#FF3864" : "#00D1A8" }}>
                                {formatNumberToKAndM(Math.abs(totalWagesDiff))}
                              </LabelNumber>
                            </>
                          )}
                        </div>
                        <div className="values-content">
                          <LabelNumber className="event-player-card-details">
                            €{teamFinancialSimulationData?.beforValue?.totalWagesText || '--'}
                          </LabelNumber>
                        </div>
                      </div>
                    </div>
                    <div className='content-value'>
                      <LabelText className="secondary-cta-text">Av. Age</LabelText>
                      <div className='pitch-selection-container' style={{ padding: '0px' }}>
                        <div>
                          {inPlayerIds && inPlayerIds.length > 0 && outPlayerIds && outPlayerIds.length > 0 && avgAgeDiff !== 0 && hasPlayersFromDifferentClubs({positionResults, teamId}) && (
                            <>
                              <img height={14} width={10} src={avgAgeDiff > 0 ? UpArrow : DownArrow} alt="trend arrow" className={avgAgeDiff > 0 ? "arrow-up-bad" : "arrow-down-good"} />
                              <LabelNumber className='event-player-card-details' style={{ color: avgAgeDiff > 0 ? "#FF3864" : "#00D1A8" }}>
                                {Math.abs(avgAgeDiff)}
                              </LabelNumber>
                            </>
                          )}
                        </div>
                        <div className="values-content">
                          <LabelNumber className="event-player-card-details">
                            {teamFinancialSimulationData?.beforValue?.avgAgeValue || '--'}
                          </LabelNumber>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}


        <div className="pitch-for-pdf">
          <PitchForPDF
            teamId={Number(teamId)}
            gameModelData={[]}
            possessionPlayers={possessionPlayers}
            possessionPasses={possessionPasses}
            positionResults={positionResults}
            teamChemistry={teamChemistry}
            selectedSimulationTab={selectedSimulationTab === 'gameModel' ? "systemFit" : selectedSimulationTab}
            formationData={formationData}
            selectedFormation={selectedFormation}
            seasonLabel={seasonLabel}
            coachName={coachName}
            activeformation={activeformation}
            activeArcheTypeName={activeArcheTypeName}
            overAllswappSystemFitScore={overAllswappSystemFitScore}
            overallSystemFitScore={overallSystemFitScore}
            inPlayerIds={inPlayerIds}
            outPlayerIds={outPlayerIds}
            swapHistoryLength={swapHistory?.length || 0}
          />
        </div>

      {/* SystemFit Values and Content */}
      {(selectedSimulationTab === 'systemFit' || selectedSimulationTab === 'gameModel') && (
        <div className="system-fit-values">
          <SystemFitValuesAndContent
          teamId={Number(teamId)}
          seasonId={seasonId || 0}
          teamAttributesData={teamAttributesData}
          simulationAttributesData={simulationAttributesData}
          teamFinancialSimulationData={teamFinancialSimulationData}
          overallSystemFitScore={overallSystemFitScore}
          overAllswappSystemFitScore={overAllswappSystemFitScore}
          inPlayerIds={inPlayerIds}
          outPlayerIds={outPlayerIds}
          squadPlayerComparisonData={squadPlayerComparisonData}
          simulationData={simulationData}
          comparisonTeamData={comparisonTeamData}
          clubCompareId={clubCompareId}
          activeArcheTypeName={activeArcheTypeName}
          archetypes={archetypes}
          isCompareBalckLine={isCompareBalckLine}
          swapDataLoading={swapDataLoading}
          compareClubLogoUrl={compareClubLogoUrl}
          compareClubName={compareClubName}
          compareClubseasonLabel={compareClubseasonLabel}
          // Pass computed values instead of functions
          stylisticFitValue={stylisticFitValue}
          stylisticFitForCoachValue={stylisticFitForCoachValue}
          compareStylisticFitValue={compareStylisticFitValue}
          afterScoreValue={afterScoreValue}
          improvementValue={improvementValue}
          clubNameValue={clubNameValue}
          seasonNameValue={seasonNameValue}
          clubNameForCoachValue={clubNameForCoachValue}
          seasonNameForCoachValue={seasonNameForCoachValue}
          playingStyleForCoachValue={playingStyleForCoachValue}
          targetedFormationValue={targetedFormationValue}
          targetedSeasonNameValue={targetedSeasonNameValue}
          targetTeamIdValue={targetTeamIdValue}
          targetedArcheTypeFormation={targetedArcheTypeFormation}
          targetedFormation={targetedFormation}
          activeTeamGraph={activeTeamGraph}
          archeTypeBy={archeTypeBy}
          positionResults={positionResults}
        />
        </div>
      )}

      {(selectedSimulationTab === 'gameModel' || selectedSimulationTab === 'systemFit') && getGameModelData() && (
        <>
        <div className="game-model-pitch-container" style={{ marginTop: selectedSimulationTab === 'systemFit' ? '40px' : '0px' }}>
          {isGameModelLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <LabelText className="secondary-cta-text">Loading Game Model Data...</LabelText>
            </div>
          ) : (
            <PitchForPDF
              teamId={Number(teamId)}
              gameModelData={getGameModelData()!}
              selectedSimulationTab="gameModel"
              formationData={formationData}
              selectedFormation={selectedFormation}
              swapHistoryLength={swapHistory?.length || 0}
              possessionPlayers={[]}
              possessionPasses={[]}
              positionResults={[]}
              teamChemistry={[]}
              inPlayerIds={[]}
              outPlayerIds={[]}
              overAllswappSystemFitScore={0}
              overallSystemFitScore={0}
            />
          )}
        </div>

        {/* GameModel Attributes */}
        <div className="game-model-values" style={{ marginTop: selectedSimulationTab === 'systemFit' ? '40px' : '0px' }}>
          {isGameModelLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <LabelText className="secondary-cta-text">Loading Game Model Data...</LabelText>
            </div>
          ) : getGameModelData() ? (
            <GameModelValuesAndContent
            gameModelData={getGameModelData()!}
            selectedGameModelAttributes={getSelectedGameModelAttributes()}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <LabelText className="secondary-cta-text">No Game Model Data Available</LabelText>
            </div>
          )}
        </div>
        </>
      )}

      {/* Possession stats */}
      {selectedSimulationTab === 'possessions' && (
        <div className="possession-values">
          <PossessionValuesAndContent
          matchBasicDetails={matchBasicDetails}
        />
        </div>
      )}

      </div>
  );
};

export default Preview;
